/**
 * Dados de referência **UI-only** e fixtures de teste da feature **Configuração de Nota e
 * Frequência**.
 *
 * A configuração da turma agora é carregada da API (tag
 * `class-planning-grade-and-frequence-configuration`, ver `hooks/useGradeConfigData`). Este
 * arquivo mantém apenas: (a) dados de referência sem contrapartida no backend —
 * `STAGES`, `EXAMS`, `TCC_FINAL_OPTIONS`, `TYPE_CONFIG` (usados por `utils/gradeCalc`); e
 * (b) `createInitialClassGroups`/`buildClassGroup`, usados apenas como **fixtures nos testes**
 * dos organisms. Produção não consome as fixtures.
 */

import {
  type ClassGroup,
  type Discipline,
  EActivityType,
  type GradeRefItem,
  type Lesson,
} from '../types/gradeConfig';

/** Metadados de cada tipo de vínculo: de qual coleção vêm os itens e o default de frequência. */
export interface TypeConfig {
  /** Coleção de origem dos itens selecionáveis. */
  readonly source: 'disciplines' | 'stages' | 'exams' | 'fixedOptions';
  /** Valor inicial de "entra na frequência" ao criar o vínculo. */
  readonly defAttendance: boolean;
  /** Opções fixas (apenas para `tcc_final`). */
  readonly fixedOptions?: readonly GradeRefItem[];
}

/** Opções fixas do tipo "Trabalho final". */
export const TCC_FINAL_OPTIONS: readonly GradeRefItem[] = [
  { id: 'tcc', name: 'TCC' },
  { id: 'bc', name: 'Business Case (BC)' },
];

/** Configuração por tipo de atividade (fonte de itens + default de frequência). */
export const TYPE_CONFIG: Record<EActivityType, TypeConfig> = {
  [EActivityType.Discipline]: { source: 'disciplines', defAttendance: true },
  [EActivityType.FinalWorkStage]: { source: 'stages', defAttendance: false },
  [EActivityType.FinalExam]: { source: 'exams', defAttendance: false },
  [EActivityType.FinalWork]: {
    source: 'fixedOptions',
    defAttendance: false,
    fixedOptions: TCC_FINAL_OPTIONS,
  },
};

/** Etapas fixas do trabalho final. */
export const STAGES: readonly GradeRefItem[] = [
  'Definição do tema',
  'Revisão bibliográfica',
  'Metodologia',
  'Coleta de dados',
  'Análise e redação',
  'Defesa',
].map((name, i) => ({ id: `etapa_${i + 1}`, name }));

/** Provas finais fixas. */
export const EXAMS: readonly GradeRefItem[] = [
  { id: 'pf_1', name: 'Prova Integrada Final' },
  { id: 'pf_2', name: 'Exame Final' },
];

/** Template de disciplinas (nome + quantidade de aulas) usado na geração. */
const DISC_TPL: ReadonlyArray<{ name: string; lessonCount: number }> = [
  { name: 'Estatística Aplicada', lessonCount: 8 },
  { name: 'Gestão Financeira', lessonCount: 6 },
  { name: 'Métodos Quantitativos', lessonCount: 4 },
  { name: 'Economia de Empresas', lessonCount: 6 },
  { name: 'Marketing Estratégico', lessonCount: 1 },
  { name: 'Gestão de Projetos', lessonCount: 1 },
  { name: 'Liderança e Pessoas', lessonCount: 5 },
  { name: 'Sustentabilidade no Agro', lessonCount: 4 },
];

function pad2(n: number): string {
  return (n < 10 ? '0' : '') + n;
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Distribui 100 entre `n` itens em inteiros, somando exatamente 100
 * (o resto é somado 1 a 1 aos primeiros itens).
 */
function distrib(n: number): number[] {
  const base = Math.floor(100 / n);
  const arr = Array.from({ length: n }, () => base);
  const rest = 100 - base * n;
  for (let i = 0; i < rest; i++) arr[i] = (arr[i] ?? 0) + 1;
  return arr;
}

/**
 * Gera as disciplinas de uma turma a partir do `DISC_TPL`, distribuindo os pesos das
 * provas e encadeando as datas das aulas semanalmente a partir de `start`.
 *
 * @param start - Data ISO (`YYYY-MM-DD`) de início da primeira disciplina.
 * @param nextId - Gerador de IDs estável (injetado para manter os mocks determinísticos).
 */
function generateDisciplines(start: string, nextId: (prefix: string) => string): Discipline[] {
  let cursor = start;
  return DISC_TPL.map(({ name, lessonCount }) => {
    const weights = distrib(lessonCount);
    const lessons: Lesson[] = Array.from({ length: lessonCount }, (_, i) => ({
      id: nextId('aula'),
      name: `Aula ${pad2(i + 1)}`,
      date: addDays(cursor, i * 7),
      showInDocument: true,
      countsInGrade: true,
      countsInAttendance: true,
      attendanceByView: true,
      exam: { id: nextId('pv'), name: `Prova ${pad2(i + 1)}`, weight: weights[i] ?? 0 },
    }));
    const startDate = lessons[0]?.date ?? cursor;
    const endDate = addDays(lessons[lessons.length - 1]?.date ?? cursor, 6);
    cursor = addDays(endDate, 1);
    return { id: nextId('disc'), name, startDate, endDate, lessons };
  });
}

/** Builder de turma (módulos seed + vínculos opcionais), espelhando `buildClassGroup` do protótipo. */
function buildClassGroup(
  name: string,
  course: string,
  start: string,
  withLinks: boolean,
  nextId: (prefix: string) => string,
): ClassGroup {
  const disciplines = generateDisciplines(start, nextId);
  const m1 = nextId('m');
  const m2 = nextId('m');
  const m3 = nextId('m');
  const m4 = nextId('m');
  const m5 = nextId('m');
  const modules = [
    { id: m1, name: 'Módulo 1 — Bloco Inicial' },
    { id: m2, name: 'Módulo 2 — Bloco Intermediário' },
    { id: m3, name: 'Módulo 3 — Bloco Avançado' },
    { id: m4, name: 'Etapas do trabalho final' },
    { id: m5, name: 'Prova final' },
  ];

  const mkActivity = (
    type: EActivityType,
    refId: string | null,
    moduleId: string | null,
    closing: 'disciplina' | 'curso',
    moduleWeight: number,
  ): ClassGroup['activities'][number] => ({
    id: nextId('a'),
    type,
    refId,
    moduleId,
    showInDocument: true,
    countsInGrade: true,
    countsInAttendance: TYPE_CONFIG[type].defAttendance,
    attendanceByView: type === EActivityType.Discipline,
    closing,
    moduleWeight,
    defaultGrade: null,
    defaultAttendance: null,
  });

  const activities = withLinks
    ? [
        mkActivity(EActivityType.Discipline, disciplines[0]?.id ?? null, m1, 'disciplina', 40),
        mkActivity(EActivityType.Discipline, disciplines[2]?.id ?? null, m1, 'disciplina', 30),
        mkActivity(EActivityType.Discipline, disciplines[3]?.id ?? null, m1, 'disciplina', 30),
        mkActivity(EActivityType.Discipline, disciplines[1]?.id ?? null, m2, 'disciplina', 50),
        mkActivity(EActivityType.Discipline, disciplines[4]?.id ?? null, m2, 'curso', 50),
        mkActivity(EActivityType.Discipline, disciplines[6]?.id ?? null, m3, 'disciplina', 100),
        mkActivity(EActivityType.FinalWorkStage, 'etapa_1', m4, 'curso', 100),
        mkActivity(EActivityType.FinalExam, 'pf_1', m5, 'curso', 50),
        mkActivity(EActivityType.FinalWork, 'tcc', m5, 'curso', 50),
      ]
    : [];

  return { id: nextId('turma'), name, course, disciplines, modules, activities };
}

/**
 * Cria o conjunto inicial de turmas mockadas (3 turmas), com IDs determinísticos.
 *
 * Cada chamada gera uma nova árvore independente (fixture de testes).
 */
export function createInitialClassGroups(): ClassGroup[] {
  let counter = 0;
  const nextId = (prefix: string): string => `${prefix}_${++counter}`;
  return [
    buildClassGroup('Turma 2026/1', 'Gestão Estratégica de Negócios', '2026-02-02', true, nextId),
    buildClassGroup('Turma 2026/2', 'Gestão Estratégica de Negócios', '2026-08-03', false, nextId),
    buildClassGroup('Turma 2025/2', 'Gestão do Agronegócio', '2025-08-04', true, nextId),
  ];
}

/** Conjunto inicial de turmas mockadas (snapshot estável). */
export const MOCK_CLASS_GROUPS: readonly ClassGroup[] = createInitialClassGroups();
